import { useEffect, useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { useAuth } from './hooks/useAuth';
import { LoginPage } from './components/LoginPage';
import { RegisterPage } from './components/RegisterPage';
import { WelcomePage } from './components/WelcomePage';
import { DashboardPage } from './components/DashboardPage';
import { SimulationBrowser } from './components/SimulationBrowser';
import { AdminPanelPage } from './components/AdminPanelPage';
import { GlobalHeader } from './components/GlobalHeader';
import { UserMenu } from './components/UserMenu';
import { useSimulation } from './hooks/useSimulation';
import { useAudio } from './hooks/useAudio';
import { useKeyboardMovement } from './hooks/useKeyboardMovement';
import { Scene3D } from './components/Scene3D';
import { EntityList } from './components/EntityList';
import { getWsToken } from './services/authApi';
import { getSimulation } from './services/api';
import type { Simulation } from './types/simulation';
import './App.css';
import './components/StatusBar.css';

const WS_SERVER_URL = 'wss://simulation.echodigital.ai/ws';

type AppView = 'welcome' | 'dashboard' | 'browser' | 'simulation' | 'admin';

interface AuthenticatedAppProps {
  simulation: Simulation;
  onBackToSimulations: () => void;
}

interface AuthenticatedAppPropsExtended extends AuthenticatedAppProps {
  onNavigateToAdmin: () => void;
}

function AuthenticatedApp({ simulation: initialSimulation, onBackToSimulations, onNavigateToAdmin }: AuthenticatedAppPropsExtended) {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [simulation, setSimulation] = useState(initialSimulation);
  const [cameraRotation, setCameraRotation] = useState({ yaw: 0, pitch: 0 });
  const [localPosition, setLocalPosition] = useState([0, 0, 0]);
  const [isConnecting, setIsConnecting] = useState(false);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  // Fetch simulation details if name is missing
  useEffect(() => {
    const fetchSimDetails = async () => {
      if (!simulation.name && simulation.id) {
        try {
          const fullSimulation = await getSimulation(simulation.id);
          setSimulation(fullSimulation);
        } catch (err) {
          console.error('Failed to fetch simulation details:', err);
        }
      }
    };
    fetchSimDetails();
  }, [simulation.id, simulation.name]);

  const {
    entities,
    myEntityId,
    receivedAudio,
    status,
    join,
    disconnect,
    updatePosition,
    sendAudio,
    refreshEntities,
    getInterpolationData,
  } = useSimulation(WS_SERVER_URL);

  const {
    isMicrophoneActive,
    isSpeakerActive,
    startMicrophone,
    stopMicrophone,
    toggleSpeaker,
    setOnAudioCapture,
    playAudio,
  } = useAudio();

  // Auto-connect to simulation when component mounts
  useEffect(() => {
    const connectToSimulation = async () => {
      if (!user || myEntityId || isConnecting) return;

      setIsConnecting(true);
      try {
        const wsToken = await getWsToken();
        join(user.username, [0, 0, 0], wsToken, simulation.id);
      } catch (err) {
        console.error('Failed to connect to simulation:', err);
      } finally {
        setIsConnecting(false);
      }
    };

    connectToSimulation();
  }, [user, myEntityId, isConnecting, join, simulation.id]);

  // Send microphone data immediately when captured
  useEffect(() => {
    if (!isMicrophoneActive || status !== 'connected' || !myEntityId) {
      setOnAudioCapture(null);
      return;
    }

    // Set callback to send audio immediately when available
    setOnAudioCapture((samples) => {
      const samplesArray = Array.from(samples);
      sendAudio(samplesArray);
    });

    return () => setOnAudioCapture(null);
  }, [isMicrophoneActive, status, myEntityId, setOnAudioCapture, sendAudio]);

  // Cleanup: stop microphone when component unmounts
  useEffect(() => {
    return () => {
      stopMicrophone();
    };
  }, [stopMicrophone]);

  // Play received audio
  useEffect(() => {
    if (!isSpeakerActive) return;

    const samples = new Float32Array(receivedAudio);
    playAudio(samples);
  }, [receivedAudio, isSpeakerActive, playAudio]);

  const handleLeaveSimulation = () => {
    disconnect();
    onBackToSimulations();
  };

  const handleCopyLink = () => {
    const url = new URL(window.location.origin);
    url.searchParams.set('sim', simulation.id);
    navigator.clipboard.writeText(url.toString());
    showNotification('Link copied to clipboard!');
  };

  const handleCopySimId = () => {
    navigator.clipboard.writeText(simulation.id);
    showNotification('Simulation ID copied!');
  };

  // Helper functions for status display
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'connecting': return 'CONNECTING';
      case 'connected': return 'CONNECTED';
      case 'disconnected': return 'DISCONNECTED';
      case 'error': return 'ERROR';
      default: return status.toUpperCase();
    }
  };

  const getStatusClass = (status: string): string => {
    return `status-dot--${status}`;
  };

  const getStatusIndicatorClass = (status: string): string => {
    return `status-indicator--${status}`;
  };

  // Create portal content for toolbar
  const portalTarget = document.getElementById('global-toolbar-portal');
  const toolbarContent = portalTarget ? ReactDOM.createPortal(
    <>
      <div className="toolbar-left">
        <div
          className="toolbar-link"
          onClick={handleLeaveSimulation}
          title="Leave simulation"
        >
          &lt; leave
        </div>
        <div className={`toolbar-link status-indicator ${getStatusIndicatorClass(status)}`}>
          <span className={`status-dot ${getStatusClass(status)}`}></span>
          <span className="status-text">{getStatusText(status)}</span>
        </div>
      </div>
      <div className="toolbar-right">
        <div
          className="toolbar-link"
          onClick={handleCopySimId}
          title="Copy simulation ID"
        >
          id
        </div>
        <div
          className="toolbar-link"
          onClick={handleCopyLink}
          title="Copy simulation link"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
            <polyline points="16 6 12 2 8 6"></polyline>
            <line x1="12" y1="2" x2="12" y2="15"></line>
          </svg>
        </div>
        <div
          className={`toolbar-link toolbar-link--toggle ${isMicrophoneActive ? 'toolbar-link--active' : ''}`}
          onClick={isMicrophoneActive ? stopMicrophone : startMicrophone}
          title={isMicrophoneActive ? 'Stop microphone' : 'Start microphone'}
        >
          mic
        </div>
        <div
          className={`toolbar-link toolbar-link--toggle ${isSpeakerActive ? 'toolbar-link--active' : ''}`}
          onClick={toggleSpeaker}
          title={isSpeakerActive ? 'Disable speaker' : 'Enable speaker'}
        >
          spk
        </div>
      </div>
    </>,
    portalTarget
  ) : null;

  const myEntity = useMemo(() => {
    return entities.find((e) => e.id === myEntityId);
  }, [entities, myEntityId]);

  // Create a dummy entity for local movement when not connected
  const movableEntity = useMemo(() => {
    if (myEntity) return myEntity;
    // When not connected, create a local entity for movement
    return {
      id: 'local',
      position: { x: localPosition[0], y: localPosition[1], z: localPosition[2] },
    };
  }, [myEntity, localPosition]);

  // Handle position changes - update server if connected, otherwise local state
  const handlePositionChange = (position: number[]) => {
    if (myEntity) {
      updatePosition(position);
    } else {
      setLocalPosition(position);
    }
  };

  // Enable keyboard movement with camera rotation
  useKeyboardMovement(movableEntity, handlePositionChange, cameraRotation);

  return (
    <div className="app">
      <GlobalHeader title={`${simulation.name}`}>
        <UserMenu onNavigateToAdmin={onNavigateToAdmin} />
      </GlobalHeader>
      {toolbarContent}

      <div className="app-layout" ref={fullscreenContainerRef}>
        {/* Left Sidebar */}
        <aside className="sidebar sidebar-left">
          <EntityList
            entities={entities}
            myEntityId={myEntityId}
            onRefresh={refreshEntities}
          />
        </aside>

        {/* Main 3D Scene */}
        <main className="main-scene">
          <Scene3D
            entities={entities}
            myEntityId={myEntityId || 'local'}
            getInterpolationData={getInterpolationData}
            onCameraRotationChange={setCameraRotation}
            fullscreenContainerRef={fullscreenContainerRef}
            localPosition={!myEntityId ? localPosition : undefined}
          />
        </main>
      </div>
    </div>
  );
}

// Auth gate component
function AuthGate({
  children,
  authView,
  onSwitchView,
  onAuthSuccess,
}: {
  children: React.ReactNode;
  authView: 'login' | 'register';
  onSwitchView: (view: 'login' | 'register') => void;
  onAuthSuccess: () => void;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const [wasUnauthenticated, setWasUnauthenticated] = useState(false);

  // Track if user was unauthenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setWasUnauthenticated(true);
    }
  }, [isAuthenticated, isLoading]);

  // Call onAuthSuccess when user becomes authenticated after being unauthenticated
  useEffect(() => {
    if (isAuthenticated && wasUnauthenticated) {
      onAuthSuccess();
    }
  }, [isAuthenticated, wasUnauthenticated, onAuthSuccess]);

  // Block content until auth check completes
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>&gt; AUTHENTICATING...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authView === 'register') {
      return <RegisterPage onSwitchToLogin={() => onSwitchView('login')} />;
    }
    return <LoginPage onSwitchToRegister={() => onSwitchView('register')} />;
  }

  return <>{children}</>;
}

function App() {
  const [authView, setAuthView] = useState<'login' | 'register'>('login');
  const [currentView, setCurrentView] = useState<AppView>(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const simId = urlParams.get('sim');
    if (simId) return 'simulation';

    // Check if user has completed onboarding
    const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
    if (hasCompletedOnboarding) {
      return 'dashboard';
    }

    return 'welcome';
  });
  const [selectedSimulation, setSelectedSimulation] = useState<Simulation | null>(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const simId = urlParams.get('sim');
    if (simId) {
      // Mark that we need to load this simulation
      return { id: simId, name: '', description: '' } as Simulation;
    }

    return null;
  });
  // Load saved theme on app mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'theme-hacker';
    document.body.className = savedTheme;
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Read state from URL and history state
      const urlParams = new URLSearchParams(window.location.search);
      const simId = urlParams.get('sim');

      if (simId) {
        // Navigating forward to a simulation
        setSelectedSimulation({ id: simId, name: '', description: '' } as Simulation);
        setCurrentView('simulation');
      } else {
        // Navigating back from simulation - restore the previous view
        setSelectedSimulation(null);
        // Look for previousView in the next history entry (the simulation we just left)
        const view = event.state?.view || event.state?.previousView;
        if (view && view !== 'simulation') {
          setCurrentView(view);
        } else {
          // Fallback if no state
          const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
          setCurrentView(hasCompletedOnboarding ? 'dashboard' : 'welcome');
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToView = (view: AppView) => {
    setCurrentView(view);
    // Push state to browser history to enable back button
    const url = new URL(window.location.href);
    url.searchParams.delete('sim');
    window.history.pushState({ view }, '', url.toString());
  };

  const handleGetStarted = () => {
    localStorage.setItem('hasCompletedOnboarding', 'true');
    navigateToView('dashboard');
  };

  const handleNavigateToDashboard = () => {
    setSelectedSimulation(null);
    navigateToView('dashboard');
  };

  const handleNavigateToBrowser = () => {
    setSelectedSimulation(null);
    navigateToView('browser');
  };

  const handleNavigateToAdmin = () => {
    setSelectedSimulation(null);
    navigateToView('admin');
  };

  const handleSelectSimulation = (simulation: Simulation) => {
    setSelectedSimulation(simulation);
    // Update URL with simulation ID and store current view in history state
    const url = new URL(window.location.href);
    url.searchParams.set('sim', simulation.id);
    window.history.pushState({ view: 'simulation', previousView: currentView }, '', url.toString());
    setCurrentView('simulation');
  };

  const handleAuthSuccess = () => {
    // After auth, check if there's a simulation in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const simId = urlParams.get('sim');
    if (simId && (!selectedSimulation || selectedSimulation.id !== simId)) {
      // Reload simulation from URL
      setSelectedSimulation({ id: simId, name: '', description: '' } as Simulation);
      setCurrentView('simulation');
    }
  };

  const [headerTitle, setHeaderTitle] = useState('> DASHBOARD');

  const renderView = () => {
    if (currentView === 'welcome') {
      return <WelcomePage onGetStarted={handleGetStarted} />;
    }

    if (currentView === 'simulation' && selectedSimulation) {
      return (
        <AuthenticatedApp
          simulation={selectedSimulation}
          onBackToSimulations={() => window.history.back()}
          onNavigateToAdmin={handleNavigateToAdmin}
        />
      );
    }

    // All other views share the same shell with GlobalHeader
    return (
      <>
        <GlobalHeader title={headerTitle}>
          <UserMenu onNavigateToAdmin={handleNavigateToAdmin} />
        </GlobalHeader>

        {currentView === 'dashboard' && (
          <DashboardPage
            onSelectSimulation={handleSelectSimulation}
            onNavigateToBrowser={handleNavigateToBrowser}
            setTitle={setHeaderTitle}
          />
        )}

        {currentView === 'browser' && (
          <SimulationBrowser
            onSelectSimulation={handleSelectSimulation}
            onNavigateToDashboard={handleNavigateToDashboard}
            setTitle={setHeaderTitle}
          />
        )}

        {currentView === 'admin' && (
          <AdminPanelPage
            onBack={handleNavigateToDashboard}
            setTitle={setHeaderTitle}
          />
        )}
      </>
    );
  };

  return (
    <AuthProvider>
      <NotificationProvider>
        <AuthGate authView={authView} onSwitchView={setAuthView} onAuthSuccess={handleAuthSuccess}>
          {renderView()}
        </AuthGate>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
